import * as yup from 'yup';
import React, { ChangeEvent, useState } from 'react';
import { UserDto, UsersApi } from '../../../api-client';
import { getApiConfiguration } from '../../../common';
import { useSearchUser } from './useSearchUser';
import { AxiosResponse } from 'axios';
import { ErrorResponse } from '../../../common/types/errorRespose';
import { useListUsers } from './useListUsers';
import { useSearchUserById } from './useSearchUserById';

type UseUserSearchProps = {
  setUser: React.Dispatch<React.SetStateAction<UserDto | null>>;
};

type UseUserSearchFormReturn = {
  formState: Record<string, string | undefined>;
  errors: Record<string, string | ErrorResponse | undefined>;
  onChange: (
    field: 'name' | 'email' | 'externalId' | 'id' | 'userProfileId',
  ) => (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isSearching: boolean;
  onSearchUserById: (id: string) => void;
  onSearchUserByExternalId: (id: string) => void;
  onSearchUser: (query: Record<string, string | undefined>) => void;
};

type UseUserSearchForm = ({ setUser }: UseUserSearchProps) => UseUserSearchFormReturn;

export const useUserSearchForm: UseUserSearchForm = ({ setUser }) => {
  const [formState, setFormState] = useState<Record<string, string | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string | ErrorResponse | undefined>>({});
  const usersApi = new UsersApi(getApiConfiguration());

  const onSuccess = ({ data }: AxiosResponse<UserDto>) => {
    setUser(data);
    setErrors((prevState) => {
      delete prevState.fetchError;
      return { ...prevState };
    });
  };

  const onSuccessExternalId = ({ data }: AxiosResponse<UserDto[]>) => {
    if (data.length) {
      setUser(data[0]);
      setErrors((prevState) => {
        delete prevState.fetchError;
        return { ...prevState };
      });
    } else {
      setUser(null);
      setErrors((prevState) => ({
        ...prevState,
        fetchError: { response: { status: 404 } } as ErrorResponse,
      }));
    }
  };

  const onSuccessId = (data: UserDto) => {
    setUser(data);
    setErrors((prevState) => {
      delete prevState.fetchError;
      return { ...prevState };
    });
  };

  const onError = (err: ErrorResponse) => {
    setUser(null);
    setErrors((prevState) => ({
      ...prevState,
      fetchError: err,
    }));
  };

  const { mutate: searchUser, isLoading: isSearching } = useSearchUser({
    usersApi,
  });

  const { mutate: searchUserByExternalId, isLoading: isSearchingByExternalId } = useListUsers({
    usersApi,
  });

  const { mutate: searchUserById, isLoading: isSearchingById } = useSearchUserById(usersApi);

  const emailSchema = yup
    .string()
    .matches(
      /^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Invalid email is entered',
    );

  const clearEmailError = () => {
    setErrors((prevState) => {
      delete prevState.email;
      return { ...prevState };
    });
  };

  const setEmailError = () => {
    setErrors((prevState) => ({ ...prevState, email: 'Invalid email is entered' }));
  };

  const validateEmail = (email: string) => {
    emailSchema.isValid(email).then((valid) => {
      if (valid) {
        clearEmailError();
      } else {
        setEmailError();
      }
    });
  };

  const updateFormField = (field: string, value: string) => {
    if (value.trim() === '') {
      setFormState((prevState) => {
        delete prevState[field];
        return { ...prevState };
      });
    } else {
      setFormState((prevState) => ({ ...prevState, [field]: value }));
    }
  };

  const onChange =
    (field: 'name' | 'email' | 'externalId' | 'id' | 'userProfileId') =>
    (e: ChangeEvent<HTMLInputElement>) => {
      updateFormField(field, e.target.value);
      if (field === 'email') {
        validateEmail(e.target.value);
      }
    };

  const onSearchUserById = (id: string) =>
    searchUserById(id, {
      onSuccess: onSuccessId,
      onError: onError,
    });

  const onSearchUserByExternalId = (id: string) =>
    searchUserByExternalId(id, {
      onSuccess: onSuccessExternalId,
      onError,
    });

  const onSearchUser = (query: Record<string, string | undefined>) =>
    searchUser(query, {
      onSuccess,
      onError,
    });

  const onSubmit = () => {
    formState.id
      ? onSearchUserById(formState.id)
      : formState.externalId
      ? onSearchUserByExternalId(formState.externalId)
      : onSearchUser(formState);
  };

  return {
    formState,
    errors,
    onChange,
    onSubmit,
    onSearchUserById,
    onSearchUserByExternalId,
    onSearchUser,
    isSearching: isSearching || isSearchingByExternalId || isSearchingById,
  };
};
